/**
 * Utility functions for generating meta tags for social media sharing
 */

/**
 * Generate meta tags for ID/Visiting cards
 * @param {Object} userData - User data object
 * @param {string} cardType - 'idcard' or 'vc'
 * @param {string} email - User email
 * @returns {Object} Meta tags object
 */
export const generateCardMetaTags = (userData, cardType = 'idcard', email = '') => {
  // Fallback to default image if user doesn't have a profile picture
  const userImageUrl = userData?.user_pic 
    ? `https://ebs.ba-systems.com/users/upload/${userData.user_pic}`
    : `${window.location.origin}/png/BA.png`; // Using company logo as fallback

  const cardTypeText = cardType === 'idcard' ? 'ID Card' : 'Visiting Card';
  
  const title = userData?.user_name 
    ? `${userData.user_name} - ${cardTypeText} | Business Automation Ltd`
    : `Employee ${cardTypeText} | Business Automation Ltd`;
  
  const description = userData?.user_name 
    ? `View the ${cardTypeText.toLowerCase()} of ${userData.user_name}${userData.designation ? ` (${userData.designation})` : ''} at Business Automation Ltd.`
    : `View employee ${cardTypeText.toLowerCase()} at Business Automation Ltd.`;

  const currentUrl = window.location.href;

  return {
    title,
    description,
    image: userImageUrl,
    url: currentUrl,
    siteName: 'Business Automation Ltd',
    type: 'profile',
    author: 'Business Automation Ltd',
    // Additional structured data
    person: {
      firstName: userData?.user_name?.split(' ')[0] || '',
      lastName: userData?.user_name?.split(' ').slice(1).join(' ') || '',
      jobTitle: userData?.designation || '',
      email: userData?.user_email || `${email}@ba-systems.com`,
      telephone: userData?.user_phone || '',
      worksFor: 'Business Automation Ltd'
    }
  };
};

/**
 * Generate JSON-LD structured data for better SEO
 * @param {Object} metaTags - Meta tags object from generateCardMetaTags
 * @returns {Object} JSON-LD structured data
 */
export const generateStructuredData = (metaTags) => {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": metaTags.person.firstName + ' ' + metaTags.person.lastName,
    "jobTitle": metaTags.person.jobTitle,
    "email": metaTags.person.email,
    "telephone": metaTags.person.telephone,
    "worksFor": {
      "@type": "Organization",
      "name": metaTags.person.worksFor,
      "url": "https://www.ba-systems.com"
    },
    "image": metaTags.image,
    "url": metaTags.url
  };
};

/**
 * Update document meta tags dynamically
 * @param {Object} metaTags - Meta tags object
 */
export const updateDocumentMeta = (metaTags) => {
  // Update title
  document.title = metaTags.title;
  
  // Update or create meta tags
  const metaTagsToUpdate = [
    { name: 'description', content: metaTags.description },
    { property: 'og:title', content: metaTags.title },
    { property: 'og:description', content: metaTags.description },
    { property: 'og:image', content: metaTags.image },
    { property: 'og:url', content: metaTags.url },
    { property: 'og:type', content: metaTags.type },
    { property: 'og:site_name', content: metaTags.siteName },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: metaTags.title },
    { name: 'twitter:description', content: metaTags.description },
    { name: 'twitter:image', content: metaTags.image },
    { name: 'author', content: metaTags.author }
  ];

  metaTagsToUpdate.forEach(({ name, property, content }) => {
    const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
    let metaTag = document.querySelector(selector);
    
    if (!metaTag) {
      metaTag = document.createElement('meta');
      if (name) {
        metaTag.setAttribute('name', name);
      } else {
        metaTag.setAttribute('property', property);
      }
      document.head.appendChild(metaTag);
    }
    
    metaTag.setAttribute('content', content);
  });

  // Add structured data
  const structuredData = generateStructuredData(metaTags);
  let structuredDataScript = document.querySelector('script[type="application/ld+json"]');
  
  if (!structuredDataScript) {
    structuredDataScript = document.createElement('script');
    structuredDataScript.type = 'application/ld+json';
    document.head.appendChild(structuredDataScript);
  }
  
  structuredDataScript.textContent = JSON.stringify(structuredData);
};